import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'use-debounce';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { Grid, Input, Paper, MenuItem, Select } from '@mui/material';

import { Table } from '../../components/table/table';
import { Filter } from '../../components/filter/filter';
import { FilterButton, FilterField } from '../../components/filter/types';
import { HeadCell, Row, RowCell } from '../../components/table/types';

import { UTCToDateTimeWithSecond } from '../../utils/dates';
import { useGetRateMarkupGroupsQuery, useGetRatesQuery, useLazyGetRatesQuery } from '../../api';
import type { Rate } from '../../types';
import { InfoField } from '../../components';
import { useGetProvidersQuery } from '../../api/controllers/providers';

export const ExchangeRates: FC = () => {
  const { t } = useTranslation();

  // api
  const [getRates, { data: rates, isLoading, isFetching }] = useLazyGetRatesQuery();
  const { data: rateMarkupGroups } = useGetRateMarkupGroupsQuery({});
  const { data: poviders } = useGetProvidersQuery({});

  // variables
  const coinRef = useRef<HTMLInputElement>(null);
  const [filterData, setFilterData] = useState<{
    filterExchangeFeeId?: number;
    filterProvider?: string;
  } | null>();
  const [filteredRates, setFilteredRates] = useState<Rate[] | undefined>([]);

  useEffect(() => {
    getRates({
      rate_markup_group_id: filterData?.filterExchangeFeeId ?? undefined,
      provider: filterData?.filterProvider ?? undefined,
    });
  }, [filterData]);

  const refreshFilter = () => {
    setFilterData(null);
    if (coinRef.current) {
      coinRef.current.value = '';
    }
  };

  // set rates to local state for filtering
  useEffect(() => {
    if (isLoading || isFetching) return;
    if (!rates) return;

    const searchTerm = coinRef.current?.value;
    if (searchTerm) {
      if (searchTerm === '') setFilteredRates(rates.rates);
      const filtered = rates.rates.filter((c) => {
        return (
          c.from_coin.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.to_coin.name.toLowerCase().includes(searchTerm.toLowerCase())
        );
      });
      setFilteredRates(filtered);
    } else {
      setFilteredRates(rates.rates);
    }
  }, [rates, isLoading, isFetching, coinRef.current?.value]);

  // filter logic
  const handleSearchValue = () => {
    if (!rates || !coinRef.current) return;

    const searchTerm = coinRef.current.value;

    if (searchTerm === '') setFilteredRates(rates.rates);
    const filtered = rates.rates.filter((c) => {
      return (
        c.from_coin.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        c.to_coin.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    });
    setFilteredRates(filtered);
  };

  const debouncedSearchHandler = useDebouncedCallback(() => {
    handleSearchValue();
  }, 600);

  const rateMarkupGroupsSelectOptions = rateMarkupGroups?.rateMarkupGroups.map(({ id, name }) => {
    return (
      <MenuItem key={id} value={id}>
        {name}
      </MenuItem>
    );
  });

  const filterFields: FilterField[] = [
    {
      name: 'coin',
      title: t('exchange-rates.filter.coin'),
      component: (
        <Input
          inputRef={coinRef}
          name="coin"
          onChange={debouncedSearchHandler}
          sx={{ minWidth: '280px', maxWidth: '360px' }}
          fullWidth
        />
      ),
    },
    {
      name: 'rate_markup_group_id',
      title: t('exchange-rates.filter.exchange-fee-group'),
      component: (
        <Select
          name="status"
          value={filterData?.filterExchangeFeeId ?? ''}
          onChange={(e) =>
            setFilterData((prevState) => ({
              ...prevState,
              filterExchangeFeeId: Number(e.target.value),
            }))
          }
          variant="standard"
          sx={{ minWidth: '280px', maxWidth: '360px' }}
        >
          {rateMarkupGroupsSelectOptions}
        </Select>
      ),
    },
    {
      name: 'rate_markup_group_id',
      title: t('exchange-rates.filter.provider'),
      component: (
        <Select
          name="provider"
          value={filterData?.filterProvider ?? ''}
          onChange={(e) =>
            setFilterData((prevState) => ({ ...prevState, filterProvider: e.target.value }))
          }
          variant="standard"
          sx={{ minWidth: '280px', maxWidth: '360px' }}
        >
          {poviders?.providers.map((p) => (
            <MenuItem key={p} value={p}>
              {p}
            </MenuItem>
          ))}
        </Select>
      ),
    },
  ];

  const filterButtons: FilterButton[] = [
    {
      name: 'refresh',
      text: t('exchange-rates.refresh-btn'),
      disabled: isLoading,
      onClick: refreshFilter,
      btnProps: {
        variant: 'outlined',
        color: 'success',
        startIcon: <RefreshIcon />,
      },
    },
  ];

  // table cells and rows
  const headCells: HeadCell[] = [
    {
      id: '1',
      label: t('exchange-rates.head-cells.last-update'),
    },
    {
      id: '2',
      label: t('exchange-rates.head-cells.from-coin'),
    },
    {
      id: '3',
      label: t('exchange-rates.head-cells.to-coin'),
    },
    {
      id: '4',
      label: t('exchange-rates.head-cells.raw-rate'),
      align: 'right',
    },
    {
      id: '5',
      label: t('exchange-rates.head-cells.markup'),
      align: 'right',
    },
    {
      id: '6',
      label: t('exchange-rates.head-cells.rate'),
      align: 'right',
    },
  ];

  const rows: Row[] | undefined = filteredRates?.map((it) => {
    const { last_update, from_coin, to_coin, raw_rate, markup, rate } = it;

    const cells: RowCell[] = [
      {
        name: 'last_update',
        value: last_update,
        formatting: (value: string) => {
          return UTCToDateTimeWithSecond(value);
        },
      },
      {
        name: 'from_coin_code',
        value: from_coin.name,
      },
      {
        name: 'to_coin_code',
        value: to_coin.name,
      },
      {
        name: 'raw_rate',
        value: raw_rate,
        align: 'right',
      },
      {
        name: 'markup',
        value: markup,
        formatting: (value: string) => {
          return `${value} %`;
        },
        align: 'right',
      },
      {
        name: 'rate',
        value: rate,
        align: 'right',
      },
    ];

    return { cells };
  });

  return (
    <>
      <Grid size={{ xs: 12 }}>
        <Filter filterFields={filterFields} filterButtons={filterButtons} />
      </Grid>

      {/*<Grid size={{ xs: 12}}>*/}
      {/*  <Stack direction="row" justifyContent="flex-end" flexGrow={1}>*/}
      {/*    <Button*/}
      {/*      variant="outlined"*/}
      {/*      color="success"*/}
      {/*      startIcon={<RefreshIcon />}*/}
      {/*      disabled={isLoading}*/}
      {/*      onClick={refetch}*/}
      {/*    >*/}
      {/*      {t('exchange-rates.refresh-btn')}*/}
      {/*    </Button>*/}
      {/*  </Stack>*/}
      {/*</Grid>*/}

      <Grid size={{ xs: 12 }} sx={{ '&.MuiGrid-item': { pt: 1 } }}>
        <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
          <Table headCells={headCells} rows={rows} isLoading={isLoading || isFetching} />
        </Paper>
      </Grid>
    </>
  );
};
